"use client"

import { useState, useRef, useEffect } from "react"
import { createPortal } from "react-dom"
import { ChevronRight, Users, Building2, ClipboardList, Link, UserCog, type LucideIcon } from "lucide-react"
import { DashboardIcon } from "@/components/dashboard-icon"
import { useSidebar } from "@/components/ui/sidebar"

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"

// Icon mapping for disabled items
const getIconComponent = (title: string) => {
  switch (title) {
    case 'Dashboard': return DashboardIcon
    case 'Clients': return Users
    case 'Organizations': return Building2
    case 'Orders': return ClipboardList
    case 'Link Checker': return Link
    case 'User Management': return UserCog
    default: return null
  }
}

// Component for collapsed sidebar items with flyout
function CollapsedSidebarItem({ item, isEnabled }: { item: any, isEnabled: boolean }) {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPosition({
        top: rect.top,
        left: rect.right + 8
      })
    }
  }, [isOpen])
  
  if (!item.items) {
    // Simple item without submenu
    return (
      <SidebarMenuItem>
        {isEnabled ? (
          <SidebarMenuButton tooltip={item.title} asChild className="hover:text-[#2563eb]">
            <a href={item.url} className="flex items-center w-full">
              {item.icon && <item.icon />}
            </a>
          </SidebarMenuButton>
        ) : (
          <SidebarMenuButton tooltip={item.title} className="opacity-50 cursor-not-allowed" disabled>
            {item.icon && <item.icon />}
          </SidebarMenuButton>
        )}
      </SidebarMenuItem>
    )
  }

  // Item with submenu
  return (
    <>
      <SidebarMenuItem className="relative">
        <div className="flex items-center w-full">
          {isEnabled ? (
            item.isGroup ? (
              <SidebarMenuButton tooltip={item.title} className="flex-1 hover:text-[#2563eb]" onClick={() => setIsOpen(!isOpen)}>
                {item.icon && <item.icon />}
              </SidebarMenuButton>
            ) : (
              <SidebarMenuButton tooltip={item.title} asChild className="flex-1 hover:text-[#2563eb]">
                <a href={item.url} className="flex items-center">
                  {item.icon && <item.icon />}
                </a>
              </SidebarMenuButton>
            )
          ) : (
            <SidebarMenuButton tooltip={item.title} className="flex-1 opacity-50 cursor-not-allowed" disabled>
              {item.icon && <item.icon />}
            </SidebarMenuButton>
          )}
          <button 
            ref={buttonRef}
            className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-sm z-10"
            onClick={() => setIsOpen(!isOpen)}
          >
            <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
          </button>
        </div>
      </SidebarMenuItem>
      
      {/* Flyout using portal */}
      {isOpen && typeof window !== 'undefined' && createPortal(
        <>
          {/* Backdrop to close flyout */}
          <div 
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
          />
          {/* Flyout menu */}
          <div 
            className="fixed w-auto min-w-24 bg-white border border-gray-200 rounded-md shadow-lg z-[9999]"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`
            }}
          >
            <div className="py-1">
              {item.items.map((subItem: any) => {
                // RELEASE_HIDDEN: Subitems in collapsed sidebar flyout respect both parent's and subitem's isEnabled state
                const subItemEnabled = isEnabled && subItem.isEnabled !== false
                return subItemEnabled ? (
                  <a
                    key={subItem.title}
                    href={subItem.url}
                    className="flex items-center px-2 py-1 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors whitespace-nowrap"
                    onClick={() => setIsOpen(false)}
                  >
                    <span>{subItem.title}</span>
                  </a>
                ) : (
                  <button
                    key={subItem.title}
                    className="flex items-center w-full px-2 py-1 text-sm text-gray-400 opacity-50 cursor-not-allowed whitespace-nowrap"
                    aria-disabled="true"
                    data-disabled="true"
                    onClick={(e) => e.preventDefault()}
                  >
                    <span>{subItem.title}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  )
}

export function NavMain({
  items,
}: {
  items: {
    title: string
    url: string
    icon?: LucideIcon
    isActive?: boolean
    isEnabled?: boolean
    isGroup?: boolean
    items?: {
      title: string
      url: string
      icon?: LucideIcon
      isEnabled?: boolean
    }[]
  }[]
}) {
  const { state } = useSidebar()
  const isCollapsed = state === "collapsed"

  if (!items || !Array.isArray(items)) {
    return null
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Platform</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => {
          // Use custom component when collapsed
          if (isCollapsed) {
            return (
              <CollapsedSidebarItem 
                key={item.title} 
                item={item} 
                isEnabled={item.isEnabled !== false} 
              />
            )
          }

          // Use original behavior when expanded
          return (
            <Collapsible key={`${item.title}-${isCollapsed}`} asChild defaultOpen={!isCollapsed && !!item.items} className="group/collapsible">
              <SidebarMenuItem>
                {item.items && item.isGroup ? (
                  // Non-navigable parent group - the whole row only expands/collapses
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={item.title} className="w-full hover:text-[#2563eb]">
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                      <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                ) : item.items ? (
                  // Items with suboptions - separate clickable main item from dropdown toggle
                  <div className="flex items-center w-full">
                    {item.isEnabled !== false ? (
                      <SidebarMenuButton tooltip={item.title} asChild className="flex-1 hover:text-[#2563eb]">
                        <a href={item.url} className="flex items-center">
                          {item.icon && <item.icon />}
                          <span>{item.title}</span>
                        </a>
                      </SidebarMenuButton>
                    ) : (
                      <button 
                        className="flex-1 flex items-center px-3 py-2 text-sm font-medium text-muted-foreground opacity-50 cursor-not-allowed"
                        aria-disabled="true"
                        data-disabled="true"
                        onClick={(e) => e.preventDefault()}
                      >
                        {(() => {
                          const IconComponent = getIconComponent(item.title)
                          return IconComponent ? (
                            <IconComponent className="mr-2 h-4 w-4 flex-none" />
                          ) : (
                            <div className="mr-2 h-4 w-4 bg-blue-500 text-white flex items-center justify-center text-xs flex-none">?</div>
                          )
                        })()}
                        <span>{item.title}</span>
                      </button>
                    )}
                    <CollapsibleTrigger asChild>
                      <button className="p-1 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-sm">
                        <ChevronRight className="h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </button>
                    </CollapsibleTrigger>
                  </div>
                ) : (
                  // Items without suboptions - simple clickable link
                  item.isEnabled !== false ? (
                    <SidebarMenuButton tooltip={item.title} asChild className="hover:text-[#2563eb]">
                      <a href={item.url} className="flex items-center w-full">
                        {item.icon && <item.icon />}
                        <span>{item.title}</span>
                      </a>
                    </SidebarMenuButton>
                  ) : (
                    <button 
                      className="flex items-center w-full px-3 py-2 text-sm font-medium text-muted-foreground opacity-50 cursor-not-allowed"
                      aria-disabled="true"
                      data-disabled="true"
                      onClick={(e) => e.preventDefault()}
                    >
                      {(() => {
                        const IconComponent = getIconComponent(item.title)
                        return IconComponent ? (
                          <IconComponent className="mr-2 h-4 w-4 flex-none" />
                        ) : (
                          <div className="mr-2 h-4 w-4 bg-blue-500 text-white flex items-center justify-center text-xs flex-none">?</div>
                        )
                      })()}
                      <span>{item.title}</span>
                    </button>
                  )
                )}
                {item.items && (
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items.map((subItem) => (
                        <SidebarMenuSubItem key={subItem.title}>
                          {item.isEnabled !== false && subItem.isEnabled !== false ? (
                            <SidebarMenuSubButton asChild className="hover:text-[#2563eb]">
                              <a href={subItem.url}>
                                <span>{subItem.title}</span>
                              </a>
                            </SidebarMenuSubButton>
                          ) : (
                            <button
                              className="flex w-full items-center px-2 py-1.5 text-sm text-muted-foreground opacity-50 cursor-not-allowed"
                              aria-disabled="true"
                              data-disabled="true"
                              onClick={(e) => e.preventDefault()}
                            >
                              <span>{subItem.title}</span>
                            </button>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                )}
              </SidebarMenuItem>
            </Collapsible>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
